'use client';

import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { MinusIcon, PlusIcon, ShoppingCartIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCartInventory } from 'hooks/use-cart-inventory';
import { Product, ProductVariant } from 'lib/shopify/types';
import { createUrl } from 'lib/utils';
import Image from 'next/image';
import { Fragment, useActionState, useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import {
  createCartAndSetCookie,
  redirectToCheckout,
  removeItem,
  switchVariant,
  updateItemQuantity
} from './actions';
import { useCart } from './cart-context';
import { EnhancedPricingCalculator } from './enhanced-pricing';
import { PromoCodeInput } from './promo-code-input';
import {
  VariantOptionSelector,
  formatCurrency
} from './ui-components';
import YouMayAlsoLike from './you-may-also-like';

// Type definitions for better type safety
type CartItem = {
  id?: string;
  merchandise: {
    id: string;
    product: any;
    selectedOptions: Array<{ name: string; value: string }>;
  };
  quantity: number;
  cost: {
    totalAmount: {
      amount: string;
      currencyCode: string;
    };
  };
};

type VariantUpdateStatus = Record<string, boolean>;
type MerchandiseSearchParams = Record<string, string>;

// Business configuration
const DISCOUNT_RATE = 0.35;
const LOW_STOCK_THRESHOLD = 5;
const MAX_RECOMMENDED_ITEMS = 3;
const HIGHLIGHT_DURATION = 1000;
const VARIANT_SWITCH_DELAY = 500;

// Component for cart header
const CartHeader = ({ onClose }: { onClose: () => void }) => (
  <div className="relative flex items-center justify-between h-16 sm:h-18 lg:h-[72px] border-b border-[#CCD2D9] p-4 sm:px-6 lg:px-8">
    <h2 className="text-lg sm:text-xl font-medium text-[#212529] flex items-center gap-2">
    <ShoppingCartIcon className="h-5 w-5" />
      Shopping Cart
    </h2>
    <button
      aria-label="Close cart"
      onClick={onClose}
      className="flex items-center justify-center h-6 w-6 sm:h-6 sm:w-6 bg-[#EDEEF0] rounded transition-colors hover:bg-gray-200"
    >
      <XMarkIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-600" />
    </button>
  </div>
);

// Component for empty cart state
const EmptyCartState = () => (
  <div className="flex h-full flex-col items-center justify-center px-6">
    <ShoppingCartIcon className="mb-4 sm:mb-6 h-12 sm:h-16 text-gray-400" />
    <p className="mb-3 sm:mb-4 text-center text-lg sm:text-xl font-medium text-gray-700">
      Your cart is empty
    </p>
    <p className="max-w-xs text-center text-gray-500 text-sm sm:text-base">
      Your shopping cart is waiting to be filled with amazing products.
    </p>
  </div>
);

// Component for quantity controls
const QuantityControls = ({ 
  item, 
  isDisabled, 
  isAtInventoryLimit, 
  onDecrease, 
  onIncrease 
}: {
  item: CartItem;
  isDisabled: boolean;
  isAtInventoryLimit: boolean;
  onDecrease: () => void;
  onIncrease: () => void;
}) => (
  <div className="flex items-center space-x-1 sm:space-x-2">
    <button
      onClick={onDecrease}
      disabled={item.quantity <= 1 || isDisabled}
      className={`flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 rounded transition-colors ${
        item.quantity <= 1 || isDisabled
          ? 'cursor-not-allowed opacity-50 bg-gray-100'
          : 'bg-white hover:bg-gray-50 border-gray-300'
      }`}
      aria-label="Decrease quantity"
    >
      <MinusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
    </button>
    
    <div className="flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 bg-[#EDEEF0] rounded">
      <span className="text-xs sm:text-sm font-medium">{item.quantity}</span>
    </div>
    
    <button
      onClick={onIncrease}
      disabled={isAtInventoryLimit || isDisabled}
      className={`flex items-center justify-center h-8 w-8 sm:h-10 sm:w-10 rounded transition-colors ${
        isAtInventoryLimit || isDisabled
          ? 'cursor-not-allowed opacity-50 bg-gray-100'
          : 'bg-white hover:bg-gray-50 border-gray-300'
      }`}
      aria-label="Increase quantity"
    >
      <PlusIcon className="h-3 w-3 sm:h-4 sm:w-4" />
    </button>
  </div>
);

// Component for price display
const PriceDisplay = ({ 
  currentPrice, 
  originalPrice, 
  savings, 
  currencyCode,
  hasRealSale,
  quantity = 1
}: {
  currentPrice: string;
  originalPrice: number;
  savings: string;
  currencyCode: string;
  hasRealSale?: boolean;
  quantity?: number;
}) => {
  const currentPriceNum = parseFloat(currentPrice.replace(/[^0-9.-]+/g, ''));
  const unitPrice = currentPriceNum / quantity;
  const unitOriginalPrice = originalPrice / quantity;
  
  return (
    <div className="flex flex-col items-end space-y-1">
    <span className="text-base sm:text-lg font-medium text-[#212529] font-futura-medium">
      {currentPrice}
    </span>
    {hasRealSale && originalPrice > 0 && (
      <>
        <span className="text-sm line-through text-[#CCD2D9] font-futura-medium relative">
            {formatCurrency(originalPrice.toString(), currencyCode)}
        </span>
        <span className="text-xs sm:text-sm text-[#73777F] font-futura-medium">
          Save {savings}
        </span>
      </>
    )}
      {/* {quantity > 1 && (
        <span className="text-xs text-[#73777F] font-futura-medium">
          {hasRealSale && originalPrice > 0 ? (
            <>
              <span className="line-through">{formatCurrency(unitOriginalPrice.toString(), currencyCode)}</span>
              {' → '}
            </>
          ) : null}
          {formatCurrency(unitPrice.toString(), currencyCode)} each
        </span>
      )} */}
  </div>
);
};

// Component for updating item placeholder
const UpdatingItemPlaceholder = () => (
  <div className="relative border-b border-[#CCD2D9] bg-gray-50 p-4 sm:p-6">
    <div className="flex h-32 sm:h-40 items-center justify-center">
      <div className="text-center text-gray-500">
        <div className="mx-auto mb-2 h-6 w-6 sm:h-8 sm:w-8 animate-spin rounded-full border-b-2 border-gray-400"></div>
        <p className="text-sm sm:text-base">Updating item...</p>
      </div>
    </div>
  </div>
);

/**
 * Helper function to find a variant that matches the selected options
 */
function findVariantForOptions(
  variants: ProductVariant[],
  selectedOptions: Record<string, string>
): ProductVariant | undefined {
  return variants.find((variant) =>
    variant.selectedOptions.every((option) => {
      const optionNameLower = option.name.toLowerCase();
      return selectedOptions[optionNameLower] === option.value;
    })
  );
}

export default function CartModal() {
  const { cart, updateCartItem, isCartOpen, openCart, closeCart } = useCart();
  const { getItemInventory, refreshInventory } = useCartInventory(cart || null);
  const [addedItem, setAddedItem] = useState<string | null>(null);
  const [updatingItems, setUpdatingItems] = useState<VariantUpdateStatus>({});
  const [isPending, startTransition] = useTransition();

  // Create cart if none exists
  useEffect(() => {
    if (!cart) {
      createCartAndSetCookie();
    }
  }, [cart]);

  // Enhanced pricing calculation - memoized to avoid recalculation
  const pricingData = useMemo(() => {
    // Create product data map for enhanced pricing
    const productDataMap = new Map<string, Product>();
    
    if (cart?.lines) {
    cart.lines.forEach((item) => {
      const fullProduct = item.merchandise.product as unknown as Product;
        if (fullProduct?.handle) {
          productDataMap.set(fullProduct.handle, fullProduct);
        }
      });
          }

    return EnhancedPricingCalculator.calculateCartPricing(cart, productDataMap);
  }, [cart]);

  // Update the cart item with a new variant
  const updateVariantInCart = useCallback(
    async (item: CartItem, newVariantId: string) => {
      if (!item.id || item.merchandise.id === newVariantId) return;

      setUpdatingItems((prev) => ({ ...prev, [item.merchandise.id]: true }));

      try {
        console.log(`Switching from variant ${item.merchandise.id} to ${newVariantId}`);
        const result = await switchVariant(null, item.merchandise.id, newVariantId, item.quantity);

        if (result) {
          console.error('Error switching variants:', result);
          alert(`Error switching variants: ${result}`);
        } else {
          refreshInventory();
          console.log('Refreshing inventory after variant change');
        }
      } catch (error) {
        console.error('Error during variant update:', error);
        alert('Failed to update variant. Please try again.');
      } finally {
        setTimeout(() => {
          setUpdatingItems((prev) => ({ ...prev, [item.merchandise.id]: false }));
        }, VARIANT_SWITCH_DELAY);
      }
    },
    [refreshInventory]
  );

  return (
    <>
      <button
        aria-label="Open cart"
        onClick={openCart}
        className="relative transition-transform active:scale-95"
      >
        <div className="relative flex h-6 w-6 items-center justify-center text-gray-600">
          <ShoppingCartIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          {cart?.totalQuantity ? (
            <div className="absolute top-0 right-0 -mt-2 -mr-2 flex h-4 w-4 items-center justify-center rounded bg-blue-600 text-[11px] font-medium text-white">
              {cart.totalQuantity > 99 ? '99+' : cart.totalQuantity}
            </div>
          ) : null}
        </div>
      </button>
      
      <Transition show={isCartOpen}>
        <Dialog onClose={closeCart} className="relative z-[70]">
          <TransitionChild
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="opacity-0 backdrop-blur-none"
            enterTo="opacity-100 backdrop-blur-[.5px]"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="opacity-100 backdrop-blur-[.5px]"
            leaveTo="opacity-0 backdrop-blur-none"
          >
            <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
          </TransitionChild>
          
          <TransitionChild
            as={Fragment}
            enter="transition-all ease-in-out duration-300"
            enterFrom="translate-x-full"
            enterTo="translate-x-0"
            leave="transition-all ease-in-out duration-200"
            leaveFrom="translate-x-0"
            leaveTo="translate-x-full"
          >
            <DialogPanel className="fixed top-0 right-0 bottom-0 flex h-full w-full sm:w-[480px] lg:w-[595px] flex-col bg-white text-black shadow-xl">
              <CartHeader onClose={closeCart} />

              {!cart || cart.lines.length === 0 ? (
                <EmptyCartState />
              ) : (
                <div className="flex h-full flex-col min-h-0">
                  <div className="flex-1 overflow-auto min-h-0">
                    {/* Cart Items */}
                    {cart.lines.map((item, i) => {
                      if (updatingItems[item.merchandise.id]) {
                        return <UpdatingItemPlaceholder key={`updating-${i}`} />;
                      }

                      const merchandiseSearchParams = {} as MerchandiseSearchParams;
                      item.merchandise.selectedOptions.forEach(({ name, value }) => {
                        if (value !== 'Default Title') { // Assuming 'Default Title' is the default option
                          merchandiseSearchParams[name.toLowerCase()] = value;
                        }
                      });

                      const merchandiseUrl = createUrl(
                        `/product/${item.merchandise.product.handle}`,
                        new URLSearchParams(merchandiseSearchParams)
                      );

                      // Format prices
                      const formattedPrice = formatCurrency(
                        item.cost.totalAmount.amount,
                        item.cost.totalAmount.currencyCode
                      );

                      // Get inventory status
                      const inventoryData = getItemInventory(item);
                      const isLowStock =
                        inventoryData?.quantityAvailable !== undefined &&
                        inventoryData.quantityAvailable < LOW_STOCK_THRESHOLD;

                      const isAtInventoryLimit =
                        inventoryData?.quantityAvailable !== undefined &&
                        item.quantity >= inventoryData.quantityAvailable;

                      // Line item delete button
                      const LineItemDeleteButton = () => {
                        const [message, formAction] = useActionState(removeItem, null);
                        const merchandiseId = item.merchandise.id;
                        const actionWithVariant = formAction.bind(null, merchandiseId);

                        return (
                          <form
                            action={async () => {
                              startTransition(() => {
                                updateCartItem(merchandiseId, 'delete');
                              });
                              await actionWithVariant();
                            }}
                          >
                            <button
                              type="submit"
                              className="text-xs sm:text-sm text-[#73777F] underline transition-colors hover:text-gray-700 font-futura-book"
                            >
                              Remove
                            </button>
                            <p aria-live="polite" className="sr-only" role="status">
                              {message}
                            </p>
                          </form>
                        );
                      };

                      const fullProduct = item.merchandise.product as unknown as Product;
                      const productOptions = fullProduct.options || [];

                      // Get variants - handle both array and GraphQL connection formats
                      let productVariants: ProductVariant[] = [];
                      if (fullProduct.variants) {
                        if (Array.isArray(fullProduct.variants)) {
                          productVariants = fullProduct.variants;
                        } else {
                          const variantsData = fullProduct.variants as unknown as {
                            edges?: Array<{ node: ProductVariant }>;
                          };
                          if (variantsData.edges && Array.isArray(variantsData.edges)) {
                            productVariants = variantsData.edges.map((edge) => edge.node);
                          }
                        }
                      }

                      const currentSelectedOptions = item.merchandise.selectedOptions.reduce(
                        (acc, option) => ({
                          ...acc,
                          [option.name.toLowerCase()]: option.value
                        }),
                        {} as Record<string, string>
                      );

                      // Find the current variant based on selected options to get real compareAtPrice
                      const currentVariant = productVariants.find((variant) =>
                        variant.selectedOptions.every((option) => {
                          const optionNameLower = option.name.toLowerCase();
                          return currentSelectedOptions[optionNameLower] === option.value;
                        })
                      );

                      // Get actual compareAtPrice from current variant or product
                      const actualCompareAtPrice = 
                        currentVariant?.compareAtPrice?.amount || 
                        fullProduct.compareAtPriceRange?.maxVariantPrice?.amount;

                      // Get enhanced pricing data for this item
                      const productForPricing = item.merchandise.product as unknown as Product;
                      const itemPricing = EnhancedPricingCalculator.calculateItemPricing(item, productForPricing);
                      
                      const hasRealSale = itemPricing.hasDiscount;
                      const originalPrice = itemPricing.originalPrice;
                      const itemSavings = itemPricing.savings;
                      
                      const formattedSavings = hasRealSale ? 
                        EnhancedPricingCalculator.formatCurrency(itemSavings.toString(), item.cost.totalAmount.currencyCode) : '';

                      const getAvailableValuesForOption = (optionName: string) => {
                        const availableValues = new Set<string>();
                        productVariants.forEach((variant) => {
                          if (variant.availableForSale) {
                            const option = variant.selectedOptions.find(
                              (opt) => opt.name.toLowerCase() === optionName.toLowerCase()
                            );
                            if (option) {
                              availableValues.add(option.value);
                            }
                          }
                        });
                        return Array.from(availableValues);
                      };

                      const handleOptionChange = async (optionName: string, optionValue: string) => {
                        if (updatingItems[item.merchandise.id]) return;

                        const newSelections = {
                          ...currentSelectedOptions,
                          [optionName.toLowerCase()]: optionValue
                        };

                        const newVariant = findVariantForOptions(productVariants, newSelections);

                        if (newVariant) {
                          const completeVariant = productVariants.find((v) => v.id === newVariant.id);
                          if (completeVariant?.quantityAvailable !== undefined) {
                            console.log(
                              `Selected variant ${newVariant.id} has ${completeVariant.quantityAvailable} in stock`
                            );
                          }
                        }

                        if (
                          newVariant &&
                          newVariant.id !== item.merchandise.id &&
                          newVariant.availableForSale
                        ) {
                          updateVariantInCart(item, newVariant.id);
                        }
                      };

                      const isNewlyAdded = addedItem === item.merchandise.id;

                      return (
                        <div
                          key={i}
                          className={`relative border-b border-[#CCD2D9] transition-colors p-4 sm:p-6 ${
                            isNewlyAdded ? 'animate-pulse bg-blue-50' : ''
                          }`}
                        >
                          {/* Main content area */}
                          <div className="flex items-start gap-0 sm:gap-6">
                            {/* Product Image */}
                            <div className="relative w-20 h-20 sm:w-24 sm:h-24 lg:w-24 lg:h-24 overflow-hidden rounded-lg flex-shrink-0">
                              <Image
                                className="h-full w-full object-cover transition-transform hover:scale-105"
                                width={96}
                                height={96}
                                alt={
                                  item.merchandise.product.featuredImage?.altText ||
                                  item.merchandise.product.title
                                }
                                src={item.merchandise.product.featuredImage?.url || ''}
                              />
                            </div>

                            {/* Product Details */}
                            <div className="flex flex-grow flex-col min-w-0">
                              {/* Product Name and Price Row */}
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                 <h3 className="text-sm sm:text-base font-medium text-[#212529] font-futura-heavy truncate whitespace-normal">
                                   {item.merchandise.product.title}
                                 </h3>

                                 {/* Options Selectors */}
                                 <div className="space-y-2 sm:space-y-3 mt-3 sm:mt-4">
                                   {productOptions.map((option, idx) => {
                                     if (
                                       option.name.toLowerCase() === 'title' &&
                                       option.values.length === 1 &&
                                       option.values[0] === 'Default Title'
                                     ) {
                                       return null;
                                     }

                                     const optionNameLower = option.name.toLowerCase();
                                     const selectedValue =
                                       currentSelectedOptions[optionNameLower] || option.values[0];
                                     const availableValues = getAvailableValuesForOption(option.name);

                                     return (
                                       <div key={idx}>
                                         <VariantOptionSelector
                                           option={option}
                                           selectedValue={selectedValue}
                                           availableValues={availableValues}
                                           onChange={(value) => handleOptionChange(option.name, value)}
                                           isDisabled={updatingItems[item.merchandise.id] || false}
                                         />
                                       </div>
                                     );
                                   })}
                                 </div>

                                 {/* Quantity Controls */}
                                 <div className="flex items-center mt-3 sm:mt-4">
                                   <QuantityControls
                                     item={item}
                                     isDisabled={updatingItems[item.merchandise.id] || false}
                                     isAtInventoryLimit={isAtInventoryLimit}
                                     onDecrease={async () => {
                                       if (item.quantity > 1) {
                                         startTransition(() => {
                                           updateCartItem(item.merchandise.id, 'minus');
                                         });
                                         await updateItemQuantity(null, {
                                           merchandiseId: item.merchandise.id,
                                           quantity: item.quantity - 1
                                         });
                                       }
                                     }}
                                     onIncrease={async () => {
                                       if (!isAtInventoryLimit && !updatingItems[item.merchandise.id]) {
                                         startTransition(() => {
                                           updateCartItem(item.merchandise.id, 'plus');
                                         });
                                         await updateItemQuantity(null, {
                                           merchandiseId: item.merchandise.id,
                                           quantity: item.quantity + 1
                                         });
                                       }
                                     }}
                                   />
                                 </div>

                                 {/* Status messages */}
                                 {updatingItems[item.merchandise.id] && (
                                   <p className="text-xs sm:text-sm text-gray-500 mt-2">
                                     Checking stock...
                                   </p>
                                 )}
                                </div>
                                 
                                <div className="flex flex-col items-end flex-shrink-0">
                                  <PriceDisplay
                                    currentPrice={formattedPrice}
                                    originalPrice={originalPrice}
                                    savings={formattedSavings}
                                    currencyCode={item.cost.totalAmount.currencyCode}
                                    hasRealSale={hasRealSale}
                                    quantity={item.quantity}
                                  />
                                </div>
                              </div>

                              {/* Remove Button - Always at bottom */}
                              <div className="flex justify-end">
                                <LineItemDeleteButton />
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Recommended Products Section */}
                    <YouMayAlsoLike 
                      maxItems={MAX_RECOMMENDED_ITEMS}
                      cartItems={cart?.lines || []}
                    />
                  </div>

                  {/* Cart Footer with safe area */}
                  <div className="flex-shrink-0 bg-white border-t border-gray-200">
                    {/* Total Section */}
                    <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
                      <div className="flex flex-col sm:flex-row sm:justify-between space-y-2 sm:space-y-0">
                        <div>
                          <p className="text-sm sm:text-base text-[#73777F] font-futura-medium">
                            Total:
                          </p>
                          <div className="flex items-center space-x-2 sm:space-x-3">
                            <span className="text-base sm:text-lg font-medium text-[#212529] font-futura-medium text-lg sm:text-xl">
                              {EnhancedPricingCalculator.formatCurrency(pricingData.finalTotal, pricingData.currencyCode)}
                            </span>
                            {pricingData.hasDiscounts && (
                              <span className="text-sm line-through text-[#CCD2D9] font-futura-medium relative">
                                {EnhancedPricingCalculator.formatCurrency(
                                  (parseFloat(pricingData.finalTotal) + parseFloat(pricingData.totalDiscount)).toString(),
                                  pricingData.currencyCode
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        {pricingData.hasDiscounts && (
                          <div className="text-left sm:text-right">
                            <p className="text-sm sm:text-base text-[#73777F] font-futura-medium">
                              Total Saving:
                            </p>
                            <p className="text-sm sm:text-base font-medium font-futura-medium">
                              -{EnhancedPricingCalculator.formatCurrency(pricingData.totalDiscount, pricingData.currencyCode)}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Promo Code Section */}
                    <div className="px-4 sm:px-6 lg:px-8">
                      <PromoCodeInput 
                        cartValue={parseFloat(pricingData.finalTotal)}
                        onSuccess={() => {
                          // Refresh cart data to show updated pricing
                          refreshInventory();
                        }}
                      />
                    </div>

                    {/* Checkout Button */}
                    <div className="px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6 safe-area-bottom">
                      <form action={redirectToCheckout}>
                        <button
                          className="flex w-full items-center justify-center h-12 sm:h-14 px-4 sm:px-6 rounded-xl sm:rounded-2xl bg-[#14BBD3] hover:bg-[#13adc3] active:bg-[#119cb0] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                          type="submit"
                          disabled={!cart || cart.lines.length === 0}
                        >
                          <span className="text-sm sm:text-base font-medium font-futura-medium uppercase tracking-wide">
                            {!cart || cart.lines.length === 0
                              ? 'Your cart is empty'
                              : 'CHECK OUT'}
                          </span>
                        </button>
                      </form>
                    </div>
                    
                    {/* iOS Safe Area Bottom */}
                    <div className="h-safe-area bg-white sm:hidden"></div>
                  </div>
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </Dialog>
      </Transition>
    </>
  );
}
